import classNames from 'classnames';
import { Moment } from 'moment';
import { DescriptionField } from 'components/Forms/DescriptionField';
import { TextField } from 'components/Forms/TextField';
import { DateTimeField } from 'components/Forms/DateTimeField';
import { ConfirmationModal } from 'components/Modal/confirmationModal';
import { getIsSuper } from 'config/permissions';
import { inputDate, toDateTimeString } from 'helpers/date';
import { transformToUrl } from 'helpers/utils';
import { FC, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { validateSetWebsiteUrl, validateSetPagesRequired, AllPagesByType } from '../utils';
import { useAppSelector } from 'helpers/hooks';
import { getActiveRole } from 'selectors/admins';
import { useFieldDirty } from 'hooks/useFormHelpers';
import { useFieldRestrictions } from 'hooks/useFieldRestrictions';
import { ENV_CONFIG } from 'config/environment';
import { SwitchToggleField } from 'components/Forms/SwitchToggleField';
import { UploadFileForm } from '../Forms/UploadFileForm';
import { FullPalette } from '../Forms/ColorPickerForm/FullPalette';
import { AccordionButton } from './AccordionButton';
import { SectionsForm } from '../Forms/SectionsForm';
import { RadioField } from 'components/Forms/RadioField';
import { PAGE_TYPE_OPTIONS } from '../Forms/SectionsForm/constants';
import { SetsData } from 'store/sets/types';
import { SetPagesForm } from '../Forms/SetPagesForm';
import { whiteLabelNavigationOptions } from '../constants';

interface Props {
  index: number;
  itemVisible: number;
  formState: any;
  handleClickAccordion: (index: number) => void;
  isPreviewVisible: boolean;
  editSetPageSelected: any;
  isPageDuplicated: boolean;
  allPagesByType: AllPagesByType;
  allPagesLoading: boolean;
  setsData: SetsData[];
  setsLoading: boolean;
}

export const CommunityPageAccordion: FC<Props> = ({
  index,
  itemVisible,
  formState,
  handleClickAccordion,
  isPreviewVisible,
  editSetPageSelected,
  isPageDuplicated,
  allPagesByType,
  allPagesLoading,
  setsData,
  setsLoading,
}) => {
  const activeRole = useAppSelector(getActiveRole);
  const setsState = useMemo(
    () => ({ data: { sets: setsData }, isLoading: setsLoading }),
    [setsData, setsLoading]
  );
  const queryStringDirty = useFieldDirty('querystring');
  const [isQueryStringModalOpen, setIsQueryStringModalOpen] = useState(false);
  const [isQueryStringChangeConfirmed, setIsQueryStringChangeConfirmed] = useState(false);
  const fieldRestrictions = useFieldRestrictions(!!editSetPageSelected);
  const handleQueryStringBlur = () => {
    if (queryStringDirty && !isQueryStringChangeConfirmed && editSetPageSelected) {
      setIsQueryStringModalOpen(true);
    }
  };

  const handleStartDateChange = (date: Moment | string) => {
    formState.change(
      'startDate',
      toDateTimeString(typeof date === 'string' ? new Date(date) : date.toDate())
    );
  };

  const handleEndDateChange = (date: Moment | string) => {
    formState.change(
      'endDate',
      toDateTimeString(typeof date === 'string' ? new Date(date) : date.toDate())
    );
  };

  const websiteUrlValidator = useMemo(() => {
    return (value: any) => {
      const stringValue = typeof value === 'string' ? value : value?.target?.value || '';
      return validateSetWebsiteUrl(stringValue, setsState, editSetPageSelected, isPageDuplicated);
    };
  }, [setsState, editSetPageSelected, isPageDuplicated]);

  return (
    <div
      className={classNames('accordion-content', {
        'accordion-content--visible': itemVisible === index,
        'accordion-content--hidden': itemVisible !== index,
      })}
    >
      {index === 0 && (
        <div className="card">
          <ConfirmationModal
            isOpen={isQueryStringModalOpen}
            handleSave={() => {
              setIsQueryStringChangeConfirmed(true);
              setIsQueryStringModalOpen(false);
            }}
            handleCancel={() => {
              formState.change('querystring', formState.getState().initialValues?.querystring);
              setIsQueryStringModalOpen(false);
            }}
            warningMessage="Are you sure you want to change this page's URL? If this link has already been distributed to donors, we'd recommend to make a copy of this page to update the URL and pause this page with a redirect to the new URL."
          />
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Top Panel</span>
            <TextField label="Page Name" name="set_title" placeholder="Enter page title" />
            <DescriptionField label="Page Description" name="set_desc" />
            <span className="oath-eyebrow inkBlue">Website URL</span>
            <TextField
              name="querystring"
              type="text"
              placeholder="example.com"
              validate={websiteUrlValidator}
              disabled={!getIsSuper(activeRole) && editSetPageSelected && !isPageDuplicated}
              parse={transformToUrl}
              customOnBlur={handleQueryStringBlur}
            />
            {formState.getState().values?.querystring && (
              <div className="category form-category query-string">
                {ENV_CONFIG().COMMUNITY_PAGE}
                {formState.getState().values?.querystring}
              </div>
            )}
            <span className="oath-eyebrow inkBlue">Fundraising Bar</span>
            <div className="row">
              <div className="col-md-6 padding0 mb8">
                <SwitchToggleField
                  name="display_fundraising_bar"
                  label="Fundraising Bar Visibility"
                />
              </div>
              <div className="col-md-6 padding0 mb8">
                <SwitchToggleField
                  name="hide_recent_location"
                  label="Location Visibility Hidden"
                  disabled={!formState.getState().values?.display_fundraising_bar}
                />
                {!formState.getState().values?.display_fundraising_bar && (
                  <div className="utility-text color-dark-gray">
                    Enable fundraising bar visibility to show location
                  </div>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 padding0 mb8">
                <SwitchToggleField name="is_enabled" label="Page Enabled" />
              </div>
            </div>
            <span className="oath-eyebrow inkBlue">Page Options</span>
            <div className="row">
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <DateTimeField
                  name="startDate"
                  label="Start Date"
                  placeholder="Select start date"
                  timeFormat={false}
                  dateFormat={true}
                  closeOnSelect
                  onDateChange={handleStartDateChange}
                  displayDatetime={inputDate(formState.getState().values?.startDate)}
                />
              </div>
              <div
                className={classNames({
                  'col-md-2': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              />
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <DateTimeField
                  name="endDate"
                  label="End Date"
                  placeholder="Select end date"
                  timeFormat={false}
                  dateFormat={true}
                  closeOnSelect
                  onDateChange={handleEndDateChange}
                  displayDatetime={inputDate(formState.getState().values?.endDate)}
                  forceDirty
                />
              </div>
            </div>
            {fieldRestrictions.canSee('whitelabeled') && (
              <>
                <span className="oath-eyebrow inkBlue">White label navigation</span>
                <div className="col-md-6 padding0">
                  <RadioField
                    name="whitelabeled"
                    label="Show or hide Oath’s navigation bar on your Community page:"
                    options={whiteLabelNavigationOptions}
                    disabled={fieldRestrictions.isDisabled('whitelabeled')}
                  />
                </div>
              </>
            )}
            <div
              className={classNames('padding0 mb8', {
                'col-md-4': !isPreviewVisible,
                'col-md-12': isPreviewVisible,
              })}
            >
              <TextField
                label="Goal Alternative (Default: Goal)"
                name="goal_alt"
                placeholder="Enter alternative text"
                disabled={!formState.getState().values?.display_fundraising_bar}
              />
              {!formState.getState().values?.display_fundraising_bar && (
                <div className="utility-text color-dark-gray">
                  Enable fundraising bar visibility to show goal alternative
                </div>
              )}
            </div>
            <div className="row">
              <div className="col-md-6 padding0">
                <SwitchToggleField name="show_header_button" label="Show Header Button" />
              </div>
            </div>
            <div className="row">
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <TextField
                  label="Button Text (Default: DONATE TO ALL)"
                  name="donate_custom_1"
                  placeholder="Enter button text"
                  disabled={!formState.getState().values?.show_header_button}
                />
              </div>
              <div
                className={classNames({
                  'col-md-2': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              />
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <TextField
                  label="Button Helper (Default: Or donate to an issue of your choice below)"
                  name="donate_custom_1_helper"
                  placeholder="Enter text"
                  disabled={!formState.getState().values?.show_header_button}
                />
              </div>
            </div>
            <span className="oath-eyebrow inkBlue">Header Styling</span>
            <div className="row">
              <div className="col-md-6 padding0">
                <UploadFileForm type="set-video" hasImageField="has_header_video" />
              </div>
              <div className="col-md-6 padding0">
                <UploadFileForm type="set-logo" hasImageField="has_header_logo" />
              </div>
            </div>
            <FullPalette
              fields={[
                {
                  name: 'header_bg_color',
                  label: 'Header Background Color',
                  description: 'Header Background Color',
                },
                {
                  name: 'primary_color',
                  label: 'Primary Color',
                  description: 'Slider track, page texts and header button text',
                },
                {
                  name: 'secondary_color',
                  label: 'Secondary Color',
                  description: 'Page buttons background, learn more hover',
                },
                {
                  name: 'tertiary_color',
                  label: 'Tertiary Color',
                  description: 'Header texts and header button background',
                },
              ]}
            />
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={setsState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 1 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Community page cards</span>
            <div className="row">
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <RadioField
                  name="page_type"
                  label="Add to your Community page and search for Community Cards here:*"
                  options={PAGE_TYPE_OPTIONS}
                />
              </div>
            </div>
            <div
              className={classNames('padding0', {
                'col-md-12': isPreviewVisible,
              })}
            >
              <Field
                name="set_pages"
                validate={validateSetPagesRequired}
                subscription={{ value: true, touched: true, error: true }}
              >
                {({ input, meta }) => (
                  <SetPagesForm
                    input={input}
                    meta={meta}
                    formState={formState}
                    allPagesByType={allPagesByType}
                    allPagesLoading={allPagesLoading}
                  />
                )}
              </Field>
            </div>
            <div className="row">
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <TextField
                  label="Raised Alternative (Default: Raised)"
                  name="card_raised_alt"
                  placeholder="Enter alternative text"
                />
              </div>
              <div
                className={classNames({
                  'col-md-2': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              />
              <div
                className={classNames('padding0', {
                  'col-md-4': !isPreviewVisible,
                  'col-md-12': isPreviewVisible,
                })}
              >
                <TextField
                  label="Goal Alt (Default: Goal)"
                  name="card_goal_alt"
                  placeholder="Enter alternative text"
                />
              </div>
            </div>
            <div
              className={classNames('padding0', {
                'col-md-4': !isPreviewVisible,
                'col-md-12': isPreviewVisible,
              })}
            >
              <TextField
                label="Helper Text (Default: Why This Matters)"
                name="card_helper_text"
                placeholder="Helper alternative text"
              />
            </div>
            <FullPalette
              fields={[
                { name: 'edge_color', label: 'Top Edge Color' },
                { name: 'cta_color', label: 'CTA Button Color' },
              ]}
            />
            <AccordionButton
              onClick={() => handleClickAccordion(index + 1)}
              disabled={setsState.isLoading}
            >
              Next
            </AccordionButton>
          </div>
        </div>
      )}
      {index === 2 && (
        <div className="card">
          <div className="card-body card-body-page-detail">
            <span className="oath-eyebrow inkBlue">Share buttons</span>
            <div className="row">
              <div className="col-md-6 padding0">
                <SwitchToggleField name="has_share" label="Show share buttons" />
              </div>
            </div>
            {formState.getState().values?.has_share && (
              <>
                <div className="row">
                  <div
                    className={classNames('padding0', {
                      'col-md-4': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <TextField
                      label="Share title"
                      name="share_title"
                      placeholder="Enter share title"
                    />
                  </div>
                </div>
                <div className="padding0 col-md-12">
                  <DescriptionField
                    label="Share description"
                    name="share_description"
                    placeholder="Enter share description"
                  />
                </div>
                <div className="row">
                  <div
                    className={classNames('padding0', {
                      'col-md-4': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <TextField
                      label="Share button alternative text (Default: Share with friends)"
                      name="share_alt"
                      placeholder="Enter share button text"
                    />
                  </div>
                  <div
                    className={classNames({
                      'col-md-2': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  />
                  <div
                    className={classNames('padding0', {
                      'col-md-4': !isPreviewVisible,
                      'col-md-12': isPreviewVisible,
                    })}
                  >
                    <TextField
                      label="Share button disclaimer"
                      name="share_disclaimer"
                      placeholder="Enter share button disclaimer"
                    />
                  </div>
                </div>
              </>
            )}
            <SectionsForm isPreviewVisible={isPreviewVisible} />
            <AccordionButton
              type="submit"
              variant="submit"
              disabled={setsState.isLoading}
              isSubmitting={formState.getState().submitting}
            >
              Publish
            </AccordionButton>
          </div>
        </div>
      )}
    </div>
  );
};
